import { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, Wind, Droplets, Thermometer, CloudFog, Sunrise, Sunset, Clock } from 'lucide-react';

const CITY = 'Altstätten';
const REGION = 'St. Gallen';
const LAT = 47.3775;
const LON = 9.5483;

// Weather code to icon
const getIconFromCode = (code) => {
  if (code === 0) return Sun;
  if (code >= 1 && code <= 3) return Cloud;
  if (code >= 45 && code <= 48) return CloudFog;
  if (code >= 51 && code <= 67) return CloudRain;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 80 && code <= 82) return CloudRain;
  if (code >= 85 && code <= 86) return CloudSnow;
  if (code >= 95) return CloudRain;
  return Cloud;
};

// Weather code to description (German)
const getWeatherDescription = (code) => {
  if (code === 0) return 'Klar';
  if (code === 1 || code === 2 || code === 3) return 'Teilweise bewölkt';
  if (code >= 45 && code <= 48) return 'Nebel';
  if (code >= 51 && code <= 55) return 'Nieselregen';
  if (code >= 56 && code <= 57) return 'Gefrierender Nieselregen';
  if (code >= 61 && code <= 65) return 'Regen';
  if (code >= 66 && code <= 67) return 'Gefrierender Regen';
  if (code >= 71 && code <= 77) return 'Schnee';
  if (code >= 80 && code <= 82) return 'Regenschauer';
  if (code >= 85 && code <= 86) return 'Schneeschauer';
  if (code >= 95) return 'Gewitter';
  return 'Bewölkt';
};

export default function WeatherWidget() {
  const [weather, setWeather] = useState(null);
  const [hourlyForecast, setHourlyForecast] = useState([]);
  const [dailyForecast, setDailyForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('hourly'); // 'hourly' or 'daily'

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Using Open-Meteo API with hourly data
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}` +
          `&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,apparent_temperature,precipitation` +
          `&hourly=temperature_2m,weather_code,precipitation_probability,wind_speed_10m` +
          `&daily=weather_code,temperature_2m_max,temperature_2m_min,sunrise,sunset,precipitation_sum,precipitation_probability_max` +
          `&timezone=Europe/Zurich&forecast_days=7`
        );
        
        if (!response.ok) throw new Error('Weather fetch failed');
        
        const data = await response.json();
        
        // Current weather
        setWeather({
          temp: Math.round(data.current.temperature_2m),
          feelsLike: Math.round(data.current.apparent_temperature),
          humidity: data.current.relative_humidity_2m,
          windSpeed: Math.round(data.current.wind_speed_10m),
          precipitation: data.current.precipitation,
          code: data.current.weather_code
        });
        
        // Hourly forecast for today (next 24 hours from current hour)
        const currentHour = new Date().getHours();
        const hourlyData = [];
        for (let i = 0; i < 24; i++) {
          const idx = currentHour + i;
          if (idx < data.hourly.time.length) {
            const time = new Date(data.hourly.time[idx]);
            hourlyData.push({
              time: time.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }),
              hour: time.getHours(),
              temp: Math.round(data.hourly.temperature_2m[idx]),
              code: data.hourly.weather_code[idx],
              precipProb: data.hourly.precipitation_probability[idx],
              windSpeed: Math.round(data.hourly.wind_speed_10m[idx])
            });
          }
        }
        setHourlyForecast(hourlyData);
        
        // 7-day forecast
        const dailyData = data.daily.time.map((date, i) => ({
          date: new Date(date).toLocaleDateString('de-CH', { weekday: 'short', day: 'numeric' }),
          dayName: new Date(date).toLocaleDateString('de-CH', { weekday: 'long' }),
          maxTemp: Math.round(data.daily.temperature_2m_max[i]),
          minTemp: Math.round(data.daily.temperature_2m_min[i]),
          code: data.daily.weather_code[i],
          sunrise: new Date(data.daily.sunrise[i]).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }),
          sunset: new Date(data.daily.sunset[i]).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }),
          precipSum: data.daily.precipitation_sum[i],
          precipProb: data.daily.precipitation_probability_max[i]
        }));
        setDailyForecast(dailyData);
        
      } catch (err) {
        console.error('Weather error:', err);
        setError('Wetterdaten nicht verfügbar');
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    // Refresh every 15 minutes
    const interval = setInterval(fetchWeather, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="border border-border rounded-lg p-4 bg-card animate-pulse">
        <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
        <div className="h-32 bg-muted rounded"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-border rounded-lg p-4 bg-card">
        <h3 className="text-lg mb-2 flex items-center gap-2">
          <Cloud className="h-5 w-5" />
          WETTER
        </h3>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  const WeatherIcon = weather ? getIconFromCode(weather.code) : Cloud;
  const todaySunrise = dailyForecast[0]?.sunrise;
  const todaySunset = dailyForecast[0]?.sunset;

  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid="weather-widget">
      <h3 className="text-lg mb-3 flex items-center gap-2">
        <Cloud className="h-5 w-5 text-sky-500" />
        WETTER — {CITY}
        <span className="text-xs text-muted-foreground font-normal">({REGION})</span>
      </h3>
      
      {weather && (
        <>
          {/* Current Weather - Main Display */}
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <WeatherIcon className="h-14 w-14 text-sky-500" />
                <div>
                  <p className="text-4xl font-bold">{weather.temp}°C</p>
                  <p className="text-sm text-muted-foreground">{getWeatherDescription(weather.code)}</p>
                  <p className="text-xs text-muted-foreground">Gefühlt wie {weather.feelsLike}°C</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Droplets className="h-4 w-4" />
                  {weather.humidity}%
                </span>
                <span className="flex items-center gap-1">
                  <Wind className="h-4 w-4" />
                  {weather.windSpeed} km/h
                </span>
                {todaySunrise && (
                  <span className="flex items-center gap-1">
                    <Sunrise className="h-4 w-4 text-orange-400" />
                    {todaySunrise}
                  </span>
                )}
                {todaySunset && (
                  <span className="flex items-center gap-1">
                    <Sunset className="h-4 w-4 text-orange-600" />
                    {todaySunset}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-muted rounded-lg mb-3">
            <button
              onClick={() => setActiveTab('hourly')}
              className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
                activeTab === 'hourly' 
                  ? 'bg-background shadow text-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Clock className="h-3.5 w-3.5" />
              Stündlich
            </button>
            <button
              onClick={() => setActiveTab('daily')}
              className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
                activeTab === 'daily' 
                  ? 'bg-background shadow text-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Cloud className="h-3.5 w-3.5" />
              7 Tage
            </button>
          </div>

          {/* Hourly Forecast */}
          {activeTab === 'hourly' && (
            <div className="overflow-x-auto -mx-2 px-2">
              <div className="flex gap-2 pb-2" style={{ minWidth: 'max-content' }}>
                {hourlyForecast.slice(0, 12).map((hour, i) => {
                  const HourIcon = getIconFromCode(hour.code);
                  const isNow = i === 0;
                  return (
                    <div 
                      key={i} 
                      className={`text-center px-2 py-2 rounded-lg min-w-[60px] ${
                        isNow ? 'bg-sky-500/10 border border-sky-500/30' : 'bg-muted/50'
                      }`}
                    >
                      <p className={`text-xs font-medium ${isNow ? 'text-sky-500' : ''}`}>
                        {isNow ? 'Jetzt' : hour.time}
                      </p>
                      <HourIcon className={`h-5 w-5 mx-auto my-1 ${isNow ? 'text-sky-500' : 'text-sky-400'}`} />
                      <p className="text-sm font-bold">{hour.temp}°</p>
                      {hour.precipProb > 0 && (
                        <p className="text-xs text-sky-500 flex items-center justify-center gap-0.5">
                          <Droplets className="h-3 w-3" />
                          {hour.precipProb}%
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                ← Scrollen für mehr Stunden →
              </p>
            </div>
          )}

          {/* Daily Forecast */}
          {activeTab === 'daily' && (
            <div className="space-y-2">
              {dailyForecast.map((day, i) => {
                const DayIcon = getIconFromCode(day.code);
                const isToday = i === 0;
                return (
                  <div 
                    key={i} 
                    className={`flex items-center gap-3 p-2 rounded-lg ${
                      isToday ? 'bg-sky-500/10 border border-sky-500/30' : 'bg-muted/30'
                    }`}
                  >
                    <div className="w-20">
                      <p className={`text-sm font-medium ${isToday ? 'text-sky-500' : ''}`}>
                        {isToday ? 'Heute' : day.date}
                      </p>
                    </div>
                    <DayIcon className="h-6 w-6 text-sky-500 flex-shrink-0" />
                    <div className="flex-1 flex items-center gap-2">
                      <span className="font-bold">{day.maxTemp}°</span>
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-sky-400 to-orange-400 rounded-full"
                          style={{ 
                            width: `${Math.min(100, Math.max(20, ((day.maxTemp - day.minTemp) / 20) * 100))}%`
                          }}
                        />
                      </div>
                      <span className="text-muted-foreground">{day.minTemp}°</span>
                    </div>
                    {day.precipProb > 0 && (
                      <span className="text-xs text-sky-500 flex items-center gap-0.5 w-12 justify-end">
                        <Droplets className="h-3 w-3" />
                        {day.precipProb}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
